// Cio che il Biriba (Μπιρίμπα, Qlash-IT/QMpirimpa) PASSA al trasporto comune (`src/lib/gamehost/`): il `dominio` delle rotte, i posti ammessi, i livelli dei robot, il corpo di un tavolo nuovo,
// le frasi dei guasti della Hub e il nome di un posto. Il trasporto non conosce nessun gioco: e questo file che glielo dice. La forma e quella di `lib/pilotta/gioco.ts` (TMA#924).
//
// ⛔ Nessuna regola del Biriba (ADR-0265): la TMA non decide legalita, punti, atout ne piena. Qui non c'e niente che lo faccia: solo nomi, rotte e corpi.
// La mossa e la sua composizione (`componiAzione`) stanno in fondo, sui tipi della copia dichiarata del contratto del tavolo (`lib/mpirimpa/protocol.ts`).
import type { PostoPubblico } from '@/lib/gamehost/protocol';
import { corpoDi, creaTavoloBody, guastoOf, restDi } from '@/lib/gamehost/rest';
import type { TipoTavolo } from '@/lib/gamehost/rest';
import type { VociRifiuto } from '@/lib/gamehost/rifiuti';
import { ticketRoute } from '@/lib/gamehost/ticket';
import { CODICI_DI_RIFIUTO, MOTIVI_DEL_TAVOLO } from './protocol';
import type { AzioneDiGioco, Card, DichiarazioneMatta, Rank, Seat, SeatView } from './protocol';
import type { RiempimentoBiriba } from './riempimento';

/** Il segmento di `/v1/games/<dominio>`: `limulo-app/gioco.js` di QMpirimpa e INNESTO.md (`/v1/games/mpirimpa/*`). */
export const MPIRIMPA_DOMINIO = 'mpirimpa';
/** Il Biriba si gioca a DUE (1 contro 1) o a QUATTRO (due coppie, compagni di fronte): il contratto del fronte e `participant_number: [2, 4]` di `lib/gameFeatures.ts`. */
export const POSTI_AMMESSI: readonly number[] = [2, 4];
export type PostiBiriba = 2 | 4;

export const MPIRIMPA_REST = restDi(MPIRIMPA_DOMINIO);
export const MPIRIMPA_TICKET_ROUTE = ticketRoute(MPIRIMPA_DOMINIO);

/**
 * I livelli del robot PER I GIOCATORI: il contratto del fronte (`fronti/mpirimpa/contratto.md`, «robot: `L1` e `L2` per i giocatori, `L0` solo nei test»). Il BFF ne accetta tre
 * (`LIVELLI_ROBOT` di `limulo-app/rotte.js`: L0, L1, L2): la Hub ne offre due. Il nome visibile e `livello.<L>` nel catalogo.
 */
export const LIVELLI_ROBOT = ['L1', 'L2'] as const;
export type LivelloRobot = (typeof LIVELLI_ROBOT)[number];

// COPIA DICHIARATA di apps/qmpirimpa-gameserver/src/crea.ts @ 5f58fd33129a4e6d76bfc86b0bd0516fbd795448 (Qlash-IT/QMpirimpa): i VALORI di `TRAGUARDI_DEL_TAVOLO_PRIVATO` (riga 12, [2015, 3005, 5005]),
// che il server vuole «da superare»; i numeri MOSTRATI sono quelli decisi da Luca, L-3939 (a) (I-1307, 31/10 15:22:32Z): 2010, 3000 e 5000. Le coppie sono scritte per esteso: MAI un'aritmetica «+5»,
// che sarebbe una regola del Biriba nella TMA (ADR-0265).
/** I traguardi che l'invito (il tavolo PRIVATO) offre: il numero da mostrare e il valore da mandare. Ogni altro tavolo resta al default del server (3005). */
export const TRAGUARDI_INVITO = [
  { mostrato: 2010, valore: 2015 },
  { mostrato: 3000, valore: 3005 },
  { mostrato: 5000, valore: 5005 },
] as const;
export type TraguardoInvito = (typeof TRAGUARDI_INVITO)[number]['valore'];
/** Il traguardo scelto all'inizio: quello di ogni altro tavolo (3005, mostrato 3000). */
export const TRAGUARDO_INVITO_INIZIALE: TraguardoInvito = 3005;
/** Il numero da MOSTRARE per un traguardo che dice la vista: il tondo dalla STESSA tabella; un valore fuori tabella, cosi com'e. */
export const traguardoMostrato = (valore: number): number => TRAGUARDI_INVITO.find((x) => x.valore === valore)?.mostrato ?? valore;

/**
 * La richiesta di un tavolo nuovo. UN solo posto dove si compone il corpo (`creaTavoloMpirimpaBody`). Col `livello` e un tavolo «contro i robot» (chi crea siede, tutti gli altri sono robot); col `riempimento` e un tavolo
 * di persone che, allo scadere di un'attesa, prende i robot del pool per i posti ancora liberi (L-3961 A): SEMPRE `privato` (su invito: un tavolo che conta non ha robot) e mai insieme al `livello`.
 * Copiata dalla forma di `RichiestaTavoloScala40` (COPIA DIVERGENTE di src/lib/scala40/gioco.ts @ 41c82cada8715369e4aec86b73ecbca37bf5db3e: 4 posti, `traguardo` dell'invito, livelli L1 e L2).
 */
export type RichiestaTavoloMpirimpa = { posti: PostiBiriba; idempotencyKey: string; traguardo?: TraguardoInvito } & (
  | { tipo: TipoTavolo; livello?: undefined; riempimento?: undefined }
  | { tipo: 'privato'; livello?: undefined; riempimento?: RiempimentoBiriba }
  | { tipo: TipoTavolo; livello: LivelloRobot; riempimento?: undefined }
);

/**
 * Il corpo di un tavolo nuovo: i campi di sempre (`tipo`, `posti`, `idempotencyKey`) e, SOLO se si sceglie un livello, `robot: { livello }`. Col campo `robot` chi crea siede a un posto estratto dal core e
 * OGNI altro posto e un robot del pool, tutti o nessuno (503 `no_robot_available`). Il Biriba non ha coda: un tavolo senza robot aspetta chi entra col codice. Mai `classificato`.
 * `traguardo` SOLO per l'invito (L-3939 (a)), uno dei valori di `TRAGUARDI_INVITO`. ⚠️ Al server arriva solo da T8: oggi il BFF non lo passa, e il tavolo nasce a 3005.
 * Col `riempimento` (solo privato) il campo `riempimento: { attesaS, livello }` e SOLO quello, oggetto piatto con ESATTAMENTE quelle due chiavi (il BFF rifiuta ogni altra forma: `INVALID_RIEMPIMENTO`);
 * senza l'opzione la chiave e ASSENTE, mai `null` e mai `undefined`.
 */
export function creaTavoloMpirimpaBody(a: RichiestaTavoloMpirimpa) {
  const base = creaTavoloBody({ tipo: a.tipo, posti: a.posti, idempotencyKey: a.idempotencyKey });
  const conTraguardo = a.traguardo === undefined ? base : { ...base, traguardo: a.traguardo };
  const conLivello = a.livello === undefined ? conTraguardo : { ...conTraguardo, robot: { livello: a.livello } };
  return a.riempimento === undefined ? conLivello : { ...conLivello, riempimento: { attesaS: a.riempimento.attesaS, livello: a.riempimento.livello } };
}

// COPIA DIVERGENTE di src/lib/scala40/gioco.ts @ 41c82cada8715369e4aec86b73ecbca37bf5db3e: `rottaRiempi`, `riempiBody`, `riempiRispostaOf`, `ragionePubblica`, `eRiempimentoRifiutato`. Divergono in: il dominio (`MPIRIMPA_REST`) e i nomi `mpirimpa.table.fill`.
/**
 * La rotta di «Gioca coi robot adesso» (`mpirimpa.table.fill`, `POST …/tavoli/:tableId/riempi`, `limulo-app` 0.3.0 di QMpirimpa): accanto a `posti` e `rivincita`, stessa base del dominio. Corpo: la sola chiave di
 * idempotenza; il livello dei robot e quello della config del tavolo.
 */
export const rottaRiempi = (tableId: string): string => `${MPIRIMPA_REST.tavoli}/${tableId.toLowerCase()}/riempi`;

/** Il corpo di «Gioca coi robot adesso»: la chiave di idempotenza e nient'altro (nessun livello: lo decide la config del tavolo). */
export const riempiBody = (a: { idempotencyKey: string }) => ({ idempotencyKey: a.idempotencyKey });

/** La risposta 200 di «Gioca coi robot adesso»: quanti robot si sono seduti e se qualche posto resta libero (lo prende il riempimento a tempo, ancora armato). `null` = una forma che non si legge. */
export function riempiRispostaOf(risposta: unknown): { riempiti: number; inAttesa: boolean } | null {
  const r = corpoDi(risposta);
  return Number.isInteger(r.riempiti) && typeof r.inAttesa === 'boolean' ? { riempiti: r.riempiti as number, inAttesa: r.inAttesa } : null;
}

/**
 * La ragione PUBBLICA di un rifiuto del BFF: `details.gameReason`. E la sola cosa che il BFF lascia uscire del codice del game host: `gamehostCode` NON esce, `code` e `E_QLASH_HTTP_<status>` e il testo del server
 * non si legge mai. `null` = nessuna ragione pubblica (un errore generico).
 */
export function ragionePubblica(errore: unknown): string | null {
  const d = (errore as { data?: unknown } | null)?.data;
  const dettagli = d !== null && typeof d === 'object' ? (d as { details?: unknown }).details : undefined;
  const r = dettagli !== null && typeof dettagli === 'object' ? (dettagli as { gameReason?: unknown }).gameReason : undefined;
  return typeof r === 'string' ? r : null;
}

/**
 * Il 400 della creazione col riempimento: il BFF lo rifiuta con la ragione pubblica `INVALID_RIEMPIMENTO` (la forma del campo). Si riconosce dallo status 400 E da quella ragione, mai dal testo del server.
 * Ramo MORTO finche qlash-limulo#267 non nomina la ragione (e finche la PR di mirror LM non serve `mpirimpa.*`): oggi il 400 arriva nudo e resta «richiesta non accettata».
 */
export function eRiempimentoRifiutato(errore: unknown): boolean {
  const e = errore as { status?: unknown } | null;
  return e !== null && typeof e === 'object' && e.status === 400 && ragionePubblica(errore) === 'INVALID_RIEMPIMENTO';
}

/** Le frasi del rifiuto di «Gioca coi robot adesso»: una lista CHIUSA, ognuna e una chiave sotto `attesa.riempimento.guasto.` (il testo del server non si mostra MAI). */
export type GuastoRiempi = 'senzaRobot' | 'completo' | 'nonTrovato' | 'robotNonAmmessi' | 'senzaRiempimento' | 'rete' | 'altro';

/**
 * Perche «Gioca coi robot adesso» non e andato. Il criterio e lo STATUS e, dove serve, SOLO `details.gameReason` (`ragionePubblica`): mai `message`, mai `gamehostCode`, mai `code`.
 *  · 503 `no_robot_available` → `senzaRobot` (il pool non ha account liberi; se qualche robot e gia arrivato e un'informazione, lo decide chi chiama);
 *  · 409 → `completo` (`table_full` non e fra le ragioni pubbliche e non serve: lo dice lo status); 404 → `nonTrovato`; nessuno status → `rete`;
 *  · 400 `robot_non_ammessi` → `robotNonAmmessi`; 400 `riempimento_assente` → `senzaRiempimento`; ogni altro 400 (`classificato` compreso: irraggiungibile, il privato non conta) e ogni altro errore → `altro`.
 */
export function guastoDelRiempimento(errore: unknown): GuastoRiempi {
  const g = guastoOf(errore);
  const status = (errore as { status?: unknown } | null)?.status;
  const ragione = ragionePubblica(errore);
  if (status === 503 && ragione === 'no_robot_available') return 'senzaRobot';
  if (status === 400 && ragione === 'robot_non_ammessi') return 'robotNonAmmessi';
  if (status === 400 && ragione === 'riempimento_assente') return 'senzaRiempimento';
  if (g === 'conflitto') return 'completo';
  if (g === 'nonTrovato') return 'nonTrovato';
  if (g === 'rete') return 'rete';
  return 'altro';
}

/** Il codice di un errore RTK, ovunque stia nel corpo: si confronta il testo serializzato, come `eRobotNonDisponibili` della Pilotta. */
function haCodice(errore: unknown, status: number, codice: string): boolean {
  const e = errore as { status?: unknown; data?: unknown } | null;
  if (e === null || typeof e !== 'object' || e.status !== status) return false;
  try {
    return JSON.stringify(e.data ?? null).includes(codice);
  } catch {
    return false;
  }
}

// ADATTATA da src/lib/pilotta/gioco.ts @ 4ba86ee5d09a13047740e7379bf59d5709a5a8f1: `eRobotNonDisponibili`, lo stesso criterio (status E codice), scritto una volta in `haCodice` per i due rifiuti dei robot.
/** Il 503 di «Contro i robot»: il pool del gioco non ha abbastanza account liberi, e nessun tavolo nasce. Un ALTRO 503 (il servizio che non risponde) resta un guasto generico. */
export const eRobotNonDisponibili = (errore: unknown): boolean => haCodice(errore, 503, 'no_robot_available');
/** Il 400 di «Contro i robot» quando il game host non ammette ancora i robot alla nascita del tavolo (`robotAllaNascita` spento: `400 robot_non_ammessi`). Un altro 400 resta un guasto di richiesta. */
export const eRobotNonAmmessi = (errore: unknown): boolean => haCodice(errore, 400, 'robot_non_ammessi');

/** La linea (la squadra) di un posto: a 2 posti ognuno e la sua; a 4 le coppie sono a posti alterni (0-2 contro 1-3), i compagni di fronte. */
export const lineaDelPosto = (posto: number, posti: PostiBiriba): number => (posti === 4 ? posto % 2 : posto);

// COPIA DICHIARATA di src/lib/pilotta/gioco.ts @ 4ba86ee5d09a13047740e7379bf59d5709a5a8f1: `PREFISSO_ROBOT`, `etichettaDelPosto`, `nomeDelPosto` ed `eRobot`, identiche (a loro volta del tressette).
/** Il prefisso che il game host mette all'etichetta di un robot (kit, `ETICHETTA_BOT`, ADR-0212 §2.1: «🤖 Bot»). La TMA disegna gia il suo `BadgeBot`: l'icona sta una volta sola. */
const PREFISSO_ROBOT = /^\u{1F916}️?\s*/u;

/** L'etichetta di un posto come la zona la stampa: per un posto ROBOT senza il «🤖» iniziale (c'e il `BadgeBot`); per una PERSONA invariata (anche se il suo nickname comincia cosi). */
export function etichettaDelPosto(posto: Pick<PostoPubblico, 'tipo' | 'etichetta'>): string {
  return posto.tipo === 'robot' ? posto.etichetta.replace(PREFISSO_ROBOT, '') : posto.etichetta;
}

/**
 * Il NOME di un posto: SOLO il nickname QLASH che il game host manda in `etichetta` (ADR-0238, I-898, criterio C16 del contratto). ⛔ Mai un ripiego su altri campi: di un posto il tavolo legge `tipo` ed
 * `etichetta` e nient'altro. Un posto libero e `libero`; una PERSONA senza nickname e il segnaposto opaco `senzaNome`; un robot senza etichetta e `bot`. Le tre frasi le passa chi chiama (sono del catalogo).
 */
export function nomeDelPosto(posti: readonly PostoPubblico[], p: number, frasi: { libero: string; senzaNome: string; bot: string }): string {
  const pp = posti.find((x) => x.posto === p);
  if (!pp || pp.tipo === 'libero') return frasi.libero;
  const n = etichettaDelPosto(pp).trim();
  if (n !== '') return n;
  return pp.tipo === 'robot' ? frasi.bot : frasi.senzaNome;
}

export const eRobot = (posti: readonly PostoPubblico[], p: number): boolean => posti.find((x) => x.posto === p)?.tipo === 'robot';

/** La rotta della partita di un tavolo: «Riprendi», «Vai al tavolo» e la creazione la aprono tutte da qui. */
export const rottaTavoloMpirimpa = (tableId: string): string => `/mpirimpa/tavolo/${tableId}`;

// ── La MOSSA e la sua composizione (dalla copia dichiarata del contratto, `./protocol`) ──────────────────────────────────────────────────────────────────────────────────────

/**
 * La mossa di una persona: il posto lo mette il trasporto, dall'ultimo `table:state`. V17 (D7 (a), vista-3): l'ATTACH puo portare la `matta` scelta, cioe la VOCE di
 * `dichiarazioniMatta` letta dalla vista (`dichiarazionePer`) e un rango di quella voce. Il MELD non la porta mai: li la matta va dove la mette il motore.
 */
export type MossaMpirimpa =
  | { t: 'DRAW' }
  | { t: 'PICKUP' }
  | { t: 'DISCARD'; card: Card }
  | { t: 'MELD'; cards: Card[] }
  | { t: 'ATTACH'; meldId: number; cards: Card[]; matta?: { dichiarazione: DichiarazioneMatta; rango: Rank } };

/**
 * La voce di `dichiarazioniMatta` per un ATTACH, o null. ⛔ Nessuna regola (ADR-0265): e un LOOKUP. La voce c'e se il game host l'ha messa nella vista (per il posto di turno, decisa da `apply`
 * del motore); qui si confronta solo la coppia (meldId, carta). `carte.length === 1` e la forma del GESTO, non una regola: il campo parla dell'ATTACH di UNA carta (contratto @ 1f2900b9,
 * `DichiarazioneMatta`). Con piu carte, o senza il campo, o senza la coppia: null, e l'azione parte senza `mattaA`.
 */
export function dichiarazionePer(v: Pick<SeatView, 'dichiarazioniMatta'>, meldId: number, carte: readonly Card[]): DichiarazioneMatta | null {
  if (carte.length !== 1) return null;
  return (v.dichiarazioniMatta ?? []).find((d) => d.meldId === meldId && d.carta === carte[0]) ?? null;
}

/**
 * Le chiavi dell'azione sono ESATTAMENTE quelle del contratto (`AzioneDiGioco`): `seat` (non `posto`, come nella Pilotta), `card`, `cards`, `meldId`. Un'altra, o un campo `undefined` messo a mano, e
 * `AZIONE_MALFORMATA`. Si compone da una `switch` con le chiavi scritte per esteso, mai da uno spread della mossa.
 * V17 (D7 (a)): `mattaA` nasce SOLO qui, SOLO nell'ATTACH di una carta, e SOLO con un rango che la voce di `dichiarazioniMatta` della STESSA coppia (meldId, carta) elenca. Se la voce portata
 * dalla mossa non combacia, la chiave non esce: sul filo non va mai un rango che la vista non ha dato.
 */
export function componiAzione(seat: Seat, da: MossaMpirimpa): AzioneDiGioco {
  switch (da.t) {
    case 'DRAW':
      return { t: 'DRAW', seat };
    case 'PICKUP':
      return { t: 'PICKUP', seat };
    case 'DISCARD':
      return { t: 'DISCARD', seat, card: da.card };
    case 'MELD':
      return { t: 'MELD', seat, cards: da.cards };
    case 'ATTACH': {
      const m = da.matta;
      const dichiarata = m !== undefined && da.cards.length === 1 && m.dichiarazione.meldId === da.meldId && m.dichiarazione.carta === da.cards[0] && m.dichiarazione.mattaA.includes(m.rango);
      return dichiarata ? { t: 'ATTACH', seat, meldId: da.meldId, cards: da.cards, mattaA: m.rango } : { t: 'ATTACH', seat, meldId: da.meldId, cards: da.cards };
    }
  }
}

/** Le frasi di un rifiuto: i motivi del tavolo (`reason`) e i codici (`code`) del contratto, elenchi CHIUSI e letterali. Un codice o un motivo fuori elenco ha la frase generica. */
export const VOCI_RIFIUTO: VociRifiuto = { motivi: MOTIVI_DEL_TAVOLO, codici: CODICI_DI_RIFIUTO };

// COPIA DICHIARATA di src/lib/burraco/mossa.ts @ 4ba86ee5d09a13047740e7379bf59d5709a5a8f1: `lasciaLaManoVuota` (righe 17-28), con la mossa del Biriba. E un CONTEGGIO, non una regola (V21).
/**
 * La mossa lascia la mano VUOTA? Allora la si chiede prima (non si annulla). Non dice se il game host l'accettera ne cosa succede dopo (si chiude, o si prende il μπιριμπάκι): dice solo che, se l'accetta, la
 * mano resta senza carte.
 */
export function lasciaLaManoVuota(mano: readonly Card[], mossa: MossaMpirimpa): boolean {
  if (mano.length === 0) return false;
  switch (mossa.t) {
    case 'DISCARD':
      return mano.length === 1 && mano[0] === mossa.card;
    case 'MELD':
    case 'ATTACH':
      return mossa.cards.length >= mano.length && mano.every((c) => mossa.cards.includes(c));
    default:
      return false;
  }
}
